import {
  Datatable,
  DatatableBody,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
} from '@monksflow/monks-ui';
import { SettledContent, SETTLED_STATE } from '@/shared/ui/animations';
import type { Brief } from '@/entities/brief';
import { redesignTableStyles } from '@/shared/ui/data-table';
import { BriefsEmptyState } from '../BriefsEmptyState';
import { BriefsErrorState } from '../BriefsErrorState';
import { BriefsTableRow } from '../BriefsTableRow';
import { BriefsTableSkeletonRow } from './BriefsTableSkeletonRow';
import { useTranslation } from 'react-i18next';

interface BriefsTableProps {
  briefs: Brief[];
  isLoading: boolean;
  /**
   * Whether the wait has lasted long enough to draw placeholders. Defaults to
   * `isLoading`, so a caller that does not care shows them immediately.
   */
  showLoadingIndicator?: boolean;
  isError: boolean;
  /** True while TanStack Query is showing previous-page rows during a transition (`keepPreviousData`). */
  isPlaceholderData?: boolean;
  /**
   * True when the user has an active (debounced) search term and/or status
   * filter. Forwarded to {@link BriefsEmptyState} so it renders the
   * "No matching briefs" variant instead of the initial "No briefs yet" copy.
   */
  hasActiveFilters?: boolean;
  onRetry: () => void;
  onRowClick: (brief: Brief) => void;
  /**
   * Fires when the user clicks the "New brief" CTA in the default (non-search)
   * empty state. Forwarded to {@link BriefsEmptyState}. Optional — when absent,
   * the empty state renders without a create button.
   */
  onNewBrief?: () => void;
  /**
   * Fires when the user clears search + status from the no-results state.
   * Forwarded to {@link BriefsEmptyState}'s "Clear filters" button.
   */
  onClearFilters?: () => void;
}

const BRIEFS_COL_WIDTH = {
  type: 'w-[150px]',
  status: 'w-[110px]',
  actions: 'w-[60px]',
} as const;

const SKELETON_ROW_COUNT = 5;
const SKELETON_ROWS = Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => i);

/**
 * The Figma-redesigned header row (MFINS-2654). Columns:
 * - Name — width-less, fills the leftover space.
 * - Type / Status / Last modified — fixed widths from the shared layout tokens.
 * - Spacer — balancing empty cell that pads the data columns up to the SIDEBAR
 *   group's 612px region so the Name column aligns with the folders table
 *   (see table-layout.constants; re-balance if changed).
 * - Actions — label-less trailing kebab column. After the MFINS-2897 Option-A
 *   pivot the accessible name is an inline `<span className="sr-only">` inside
 *   the `<DatatableHead>` (design D6 — no wrapper primitive).
 *
 * `Created` was dropped (replaced by the relative-time `Last modified` column).
 * `Comments` from Figma is OMITTED for v1 because `BriefResponseDto` exposes no
 * `commentCount` field. Add the column when the BE ships it.
 */
const BriefsTableHeader = () => {
  const { t } = useTranslation('projects');

  return (
    <DatatableHeader>
      <DatatableRow>
        <DatatableHead scope="col">{t('projectDetail.briefsTable.title')}</DatatableHead>
        <DatatableHead scope="col" className={BRIEFS_COL_WIDTH.type}>
          {t('projectDetail.briefsTable.type')}
        </DatatableHead>
        <DatatableHead scope="col" className={BRIEFS_COL_WIDTH.status}>
          {t('projectDetail.briefsTable.status')}
        </DatatableHead>
        <DatatableHead scope="col" className={BRIEFS_COL_WIDTH.actions}>
          <span className="sr-only">{t('projectDetail.briefsTable.actions')}</span>
        </DatatableHead>
      </DatatableRow>
    </DatatableHeader>
  );
};

/**
 * Orchestrates the briefs list table for the briefs view.
 *
 * State precedence:
 *   1. `isError` && no rows → render {@link BriefsErrorState} with a retry hook.
 *   2. `isLoading` && no prior rows → render {@link SKELETON_ROW_COUNT} skeleton rows.
 *   3. `briefs.length === 0` → render {@link BriefsEmptyState}.
 *   4. Otherwise → render the table populated with one {@link BriefsTableRow} per brief.
 *
 * When `isPlaceholderData` is true (TanStack `keepPreviousData` is showing the
 * previous page during a transition) OR we're loading with no prior rows, the
 * `<table>` exposes `aria-busy="true"` so assistive tech announces the
 * stale-but-valid state. When false, the attribute is omitted entirely — per
 * MFINS-2897 Decision 5, we never emit `aria-busy="false"` because that is not
 * equivalent to the absence of the attribute and would announce a non-busy
 * state on every render.
 *
 * Migrated to monks-ui `Datatable*` (MFINS-2897 Option-A pivot):
 * - `<Datatable>` is the raw monks-ui organism; `aria-busy` flows through
 *   directly to the underlying `<table>` (no wrapper).
 * - `<DatatableHeader>` + `<DatatableHead>` render the semantic
 *   `border-b-2 border-border` header divider — replaces the previous
 *   `border-black/19` opacity hack with a theme-aware token.
 * - Skeleton rows use {@link BriefsTableSkeletonRow} built on
 *   `TableSkeletonRow` + `TableSkeletonActionsCell` from
 *   `@/shared/ui/data-table`.
 */
export function BriefsTable(props: BriefsTableProps) {
  const {
    briefs,
    isLoading,
    showLoadingIndicator = isLoading,
    isError,
    isPlaceholderData = false,
    hasActiveFilters = false,
    onRetry,
    onRowClick,
    onNewBrief,
    onClearFilters,
  } = props;

  const hasRows = briefs.length > 0;
  const state =
    isError && !hasRows
      ? SETTLED_STATE.error
      : isLoading && !hasRows
        ? SETTLED_STATE.loading
        : hasRows
          ? SETTLED_STATE.rows
          : SETTLED_STATE.empty;

  const content =
    state === 'error' ? (
      <BriefsErrorState onRetry={onRetry} />
    ) : state === 'loading' ? (
      // Until the wait is worth announcing there is nothing honest to draw:
      // the answer could still be rows or nothing at all, and guessing either
      // shows the user a state the data never had.
      !showLoadingIndicator ? null : (
        <Datatable aria-busy className={redesignTableStyles.table}>
          <BriefsTableHeader />
          <DatatableBody>
            {SKELETON_ROWS.map((i) => (
              <BriefsTableSkeletonRow key={i} />
            ))}
          </DatatableBody>
        </Datatable>
      )
    ) : state === 'empty' ? (
      <BriefsEmptyState
        hasActiveFilters={hasActiveFilters}
        onNewBrief={onNewBrief}
        onClearFilters={onClearFilters}
      />
    ) : (
      <Datatable aria-busy={isPlaceholderData || undefined} className={redesignTableStyles.table}>
        <BriefsTableHeader />
        <DatatableBody>
          {briefs.map((brief) => (
            <BriefsTableRow key={brief.id} brief={brief} onClick={onRowClick} />
          ))}
        </DatatableBody>
      </Datatable>
    );

  return (
    <SettledContent
      state={state}
      className="min-h-0 flex-1"
      contentClassName="flex min-h-0 flex-col"
    >
      {content}
    </SettledContent>
  );
}
